<template>
  <div class='c_tab__content--flex'>
    <div v-if="tab?.media" class="c_tab__content__media">
      <div v-if="tab?.mediaType === 'image'">
        <MediaImage :sources="[tab?.media]" :alt="tab?.mediaAlt" :mediaObjectFit="tab?.mediaObjectFit || null" />
      </div>
      <div v-if="tab?.mediaType === 'videoUpload' && tab?.videoUrl">
        <video ref="videoRef" source="file" :poster="tab.media.srcWebp" playsinline autoplay loop muted :style="{'--media-object-fit': tab?.mediaObjectFit || null }">
          <source :src="tab?.videoUrl" type="video/mp4" />
        </video>
        <button @click="toggleVideo(); toggleButtonPause()" class="c_page_header_playback" :aria-label="$t('Media - Play')">
          <IconSVG :handle="!isPlaying ? 'hotspot_play' : 'hotspot_pause'" color="white" />
        </button>
      </div>
      <div v-if="tab?.mediaType === 'video'">
        <MediaVideo source="youtube" v-if="isPlaying" :video-id="tab?.videoId" autoplay playsinline />
        <Button
          reset
          unstyle
          class="c_tab_video__play--youtube"
          v-if="!isPlaying"
          @onClick="toggleVideo"
          :aria-label="`${$t('Media - Play')}`"
        >
          <MediaImage :sources="[tab.media]" :alt="tab?.mediaAlt" :mediaObjectFit="tab?.mediaObjectFit || null" />
          <IconSVG handle="media_play_2022" />
        </Button>
      </div>
    </div>
    <div class="c_tab__content__text" v-if="tab?.contentTitle || tab?.body">
      <h3 class="c_2022--h3_graphik" v-if="tab?.contentTitle">{{ tab?.contentTitle }}</h3>
      <div class="c_2022--body" v-if="tab?.body" v-html="tab?.body"></div>
      <a
        :href="tab?.cta.url"
        class="c_tab__cta"
        v-if="tab?.cta?.url"
        :target="tab?.cta?.target"
        :aria-label="tab?.cta?.ariaLabel"
      >
        {{ tab?.cta.customText || tab?.cta.text }}
      </a>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref } from '@nuxtjs/composition-api';
import MediaImage from 'Components/image/MediaImage.vue';
import MediaVideo from 'Components/video/MediaVideo.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import Button from 'Components/button/Button.vue';

export default defineComponent({
  name: 'TabContent',
  components: {
    MediaImage,
    MediaVideo,
    IconSVG,
    Button,
  },
  props: {
    tab: { type: Object, default: {} },
    isPlaying: { type: Boolean, default: false },
  },
  setup(props) {
    const isPlaying = ref(true);
    const videoRef = ref(null);
    const pausedByButton = ref(null);

    // Handler to set all tabs back to default
    // Fired by parent, `Tabs.vue`
    const resetTab = () => {
      isPlaying.value = false;
    };

    const toggleVideo = () => {
      isPlaying.value = !isPlaying.value;
    };

    const toggleButtonPause = () => {
      pausedByButton.value = !pausedByButton.value
    }

    return { toggleVideo, isPlaying, resetTab, videoRef, pausedByButton, toggleButtonPause };
  },
  watch: {
    isPlaying(newV){
      if (!this.videoRef) return;
      if (newV) this.videoRef.play();
      else this.videoRef.pause();
    }
  },
  mounted(){
    if (!this.$refs.videoRef) return

    const videoEl = this.$refs.videoRef;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (this.isPlaying) return
          else {
            if (this.pausedByButton) return
            else this.toggleVideo()
          }
        }
        if (!entry.isIntersecting){
          if (this.isPlaying) this.toggleVideo()
          else return
        }
      });
    }, { threshold: 0.5 });
    observer.observe(videoEl);
  }
});
</script>

<style lang="scss">
.c_tab__content {
  .c_tab__content__media {
    --media-object-fit: 'cover';
    flex-basis: 100%;
    height: fit-content;
    aspect-ratio: 1 / 1;

    & > div {
      height: 100%;
      aspect-ratio: 1 / 1;
      max-width: 100%;
    }
    @include mq($mq_sm_to_md) {
      flex-basis: 50%;
    }
    position: relative;
    max-width: 100%;
    // max-height: 550px;

    picture,
    video,
    .c_video {
      aspect-ratio: 1/1;
      display: block;
      padding-bottom: 0;
      border-radius: px_to_rem(10);
      overflow: hidden;
      background-color: $color_black;
    }
    img,
    video {
      height: 100%;
      width: 100%;
      object-fit: var(--media-object-fit);
    }
    .c_page_header_playback {
      padding: 0;
      left: px_to_rem(20);
      bottom: px_to_rem(20);
      width: px_to_rem(36);
      height: px_to_rem(36);
      position: absolute;
      z-index: 100;
      border-radius: 50%;
      background-color: #393939;
      border: 1px solid #555555;
      color: $color_white;
      align-items: center;
      justify-content: center;
      display: flex;
      &:hover {
        background-color: lighten(#393939, 3%);
      }
      svg {
        width: px_to_rem(10);
        height: px_to_rem(10);
      }
      @include mq(1023) {
        bottom: px_to_rem(25);
        right: px_to_rem(25);
      }
    }
  }
  .c_tab_video__play {
    position: relative;
    height: 100%;
    width: 100%;
    background-color: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    border-radius: px_to_rem(10);
    overflow: hidden;
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      background-color: rgba(255, 255, 255, 0);
      transition: background-color ease-in-out 0.15s;
      height: 100%;
      width: 100%;
    }
    img {
      transition: transform ease-in-out 0.15s;
    }
    &:hover {
      @media (hover: hover) and (pointer: fine) {
        img {
          transform: scale(1.05);
        }
        &:after {
          background-color: rgba(255, 255, 255, 0.05);
        }
        .c_icon_svg {
          background-color: lighten(#393939, 3%);
        }
      }
    }
    .c_icon_svg {
      position: absolute;
      display: flex;
      bottom: px_to_rem(20);
      left: px_to_rem(20);
      z-index: 10;
      cursor: pointer;
      @include transition(all 0.1s ease);
      align-items: center;
      justify-content: center;
      height: px_to_rem(40);
      width: px_to_rem(40);
      border-radius: 50%;
      background-color: #393939;
      border: 1px solid #555555;
      color: $color_white;

      svg {
        width: px_to_rem(10);
        height: px_to_rem(10);
      }
      @include mq($mq_md_to_lg) {
        display: flex;
      }
    }
    &--youtube {
      overflow: hidden;

      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        background-color: rgba(255, 255, 255, 0);
        transition: background-color ease-in-out 0.15s;
        height: 100%;
        width: 100%;
        border-radius: .5rem;

      }
      .c_icon_svg {
        position: absolute;
        top: 0;
        bottom: 0;
      }
      svg {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
        width: px_to_rem(50);
        height: px_to_rem(50);
        color: $color_red;
        border: 2px solid white;
        border-radius: 50%;
        fill: $color_white;
        transition: color ease-in-out 0.05s;
        @include mq($mq_sm_to_md) {
          width: px_to_rem(80);
          height: px_to_rem(80);
        }
      }
      img {
        transition: transform ease-in-out 0.15s;
      }
      &:hover {
        @media (hover: hover) and (pointer: fine) {
          img {
            transform: scale(1.05);
          }
          svg {
            color: $color_red_255;
          }
          &:after {
            background-color: rgba(255, 255, 255, 0.05);
          }
        }
      }
    }
  }
  .c_tab__content__text {
    flex-basis: 100%;
    @include mq($mq_sm_to_md) {
      flex-basis: 50%;
    }
    display: flex;
    flex-direction: column;
    justify-content: center;
    // max-width: 550px;
    justify-self: center;
  }
}
</style>
